import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import QuratorLayout from '../components/qurator/QuratorLayout';
import PendingValidationSlider from '../components/qurator/PendingValidationSlider';
import API from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import './QuratorDashboard.css';

/* ─────────────────────────────────────────────
   Portal Tooltip — renders at <body> level so
   it's NEVER clipped by any overflow: hidden.
   Uses cloneElement — NO wrapper span, so
   getBoundingClientRect() always works correctly.
   ───────────────────────────────────────────── */
const Tooltip = ({ text, children, position = 'top' }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, pos: 'top' });

  const show = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    // position: fixed — coords ARE viewport coords, no scroll offset needed
    const left = rect.left + rect.width / 2;
    const top  = position === 'bottom' ? rect.bottom + 10 : rect.top - 10;
    setCoords({ top, left, pos: position === 'bottom' ? 'bottom' : 'top' });
    setVisible(true);
  }, [position]);

  const hide = useCallback(() => setVisible(false), []);

  // Inject handlers directly onto the single child — no wrapper element
  const child = React.Children.only(children);
  const enhanced = React.cloneElement(child, {
    onMouseEnter: (e) => { show(e); child.props.onMouseEnter?.(e); },
    onMouseLeave: (e) => { hide();  child.props.onMouseLeave?.(e); },
    onFocus:      (e) => { show(e); child.props.onFocus?.(e); },
    onBlur:       (e) => { hide();  child.props.onBlur?.(e); },
  });

  return (
    <>
      {enhanced}
      {visible && ReactDOM.createPortal(
        <div
          className={`qd-portal-tooltip qd-portal-tooltip--${coords.pos}`}
          style={{ top: coords.top, left: coords.left }}
        >
          {text}
        </div>,
        document.body
      )}
    </>
  );
};

Chart.register(...registerables);

const QuratorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_experiments: 0,
    total_projects: 0,
    total_teams: 0,
    total_qure_users: 0,
    scheduled_experiments: 0,
    completed_experiments: 0,
    published_percentage: 0,
    engagement_percentage: 0,
    reproducibility_score: 0
  });
  const [projectProgression, setProjectProgression] = useState([]);
  const [copilotLeaderboard, setCopilotLeaderboard] = useState([]);
  // const [recentTeams, setRecentTeams] = useState([]); // Reserved for future use
  const [recentScheduled, setRecentScheduled] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [isAggregate, setIsAggregate] = useState(false);
  
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const calculateAggregateData = React.useCallback((data) => {
    let aggregatedData = [];
    let copilotSum = 0;
    let projectSum = 0;
    let scheduledSum = 0;
    let assignedCompletedSum = 0;
    let projectCompletedSum = 0;

    data.forEach(item => {
      copilotSum += item.experiments;
      projectSum += item.projects;
      scheduledSum += item.scheduled;
      assignedCompletedSum += item.assigned_completed;
      projectCompletedSum += item.project_completed;

      aggregatedData.push({
        month: item.month,
        experiments: copilotSum,
        projects: projectSum,
        scheduled: scheduledSum,
        assigned_completed: assignedCompletedSum,
        project_completed: projectCompletedSum
      });
    });

    return aggregatedData;
  }, []);

  const initChart = React.useCallback(() => {
    if (!chartRef.current || analyticsData.length === 0) return;
    
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const dataToUse = isAggregate ? calculateAggregateData(analyticsData) : analyticsData;
    const ctx = chartRef.current.getContext('2d');

    const gradient1 = ctx.createLinearGradient(0, 0, 0, 400);
    gradient1.addColorStop(0, 'rgba(255, 87, 34, 0.3)');
    gradient1.addColorStop(1, 'rgba(255, 87, 34, 0.05)');

    const gradient2 = ctx.createLinearGradient(0, 0, 0, 400);
    gradient2.addColorStop(0, 'rgba(255, 152, 0, 0.3)');
    gradient2.addColorStop(1, 'rgba(255, 152, 0, 0.05)');

    const gradient3 = ctx.createLinearGradient(0, 0, 0, 400);
    gradient3.addColorStop(0, 'rgba(33, 150, 243, 0.3)');
    gradient3.addColorStop(1, 'rgba(33, 150, 243, 0.05)');

    const gradient4 = ctx.createLinearGradient(0, 0, 0, 400);
    gradient4.addColorStop(0, 'rgba(76, 175, 80, 0.3)');
    gradient4.addColorStop(1, 'rgba(76, 175, 80, 0.05)');

    const gradient5 = ctx.createLinearGradient(0, 0, 0, 400);
    gradient5.addColorStop(0, 'rgba(156, 39, 176, 0.3)');
    gradient5.addColorStop(1, 'rgba(156, 39, 176, 0.05)');

    chartInstanceRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: dataToUse.map(item => item.month),
        datasets: [
          {
            label: 'Copilot',
            data: dataToUse.map(item => item.experiments),
            borderColor: '#FF5722',
            backgroundColor: gradient1,
            tension: 0.4,
            fill: true,
            pointBackgroundColor: '#FF5722',
            pointBorderColor: '#FFFFFF',
            pointBorderWidth: 3,
            pointRadius: 6,
            pointHoverRadius: 8
          },
          {
            label: 'Projects',
            data: dataToUse.map(item => item.projects),
            borderColor: '#FF9800',
            backgroundColor: gradient2,
            tension: 0.4,
            fill: true,
            pointBackgroundColor: '#FF9800',
            pointBorderColor: '#FFFFFF',
            pointBorderWidth: 3,
            pointRadius: 6,
            pointHoverRadius: 8
          },
          {
            label: 'Assigned',
            data: dataToUse.map(item => item.scheduled),
            borderColor: '#2196F3',
            backgroundColor: gradient3,
            tension: 0.4,
            fill: true,
            pointBackgroundColor: '#2196F3',
            pointBorderColor: '#FFFFFF',
            pointBorderWidth: 3,
            pointRadius: 6,
            pointHoverRadius: 8
          },
          {
            label: 'Assigned Completed',
            data: dataToUse.map(item => item.assigned_completed),
            borderColor: '#4CAF50',
            backgroundColor: gradient4,
            tension: 0.4,
            fill: true,
            pointBackgroundColor: '#4CAF50',
            pointBorderColor: '#FFFFFF',
            pointBorderWidth: 3,
            pointRadius: 6,
            pointHoverRadius: 8
          },
          {
            label: 'Project Completed',
            data: dataToUse.map(item => item.project_completed),
            borderColor: '#9C27B0',
            backgroundColor: gradient5,
            tension: 0.4,
            fill: true,
            pointBackgroundColor: '#9C27B0',
            pointBorderColor: '#FFFFFF',
            pointBorderWidth: 3,
            pointRadius: 6,
            pointHoverRadius: 8
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            bottom: 20
          }
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 20,
              usePointStyle: true,
              font: {
                size: 12,
                weight: '500'
              }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: {
              size: 14,
              weight: '600'
            },
            bodyFont: {
              size: 13
            },
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: true
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              font: {
                size: 11,
                weight: '500'
              },
              color: '#718096'
            }
          },
          y: {
            beginAtZero: true,
            grid: {
              color: 'rgba(0, 0, 0, 0.05)',
              drawBorder: false
            },
            ticks: {
              font: {
                size: 11,
                weight: '500'
              },
              color: '#718096',
              stepSize: 1
            }
          }
        },
        interaction: {
          intersect: false,
          mode: 'index'
        }
      }
    });
  }, [analyticsData, isAggregate, calculateAggregateData]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (analyticsData.length > 0 && chartRef.current) {
      initChart();
    }
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [analyticsData, isAggregate, initChart]);

  // Scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    // Observe all animate-on-scroll elements
    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, [loading]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [statsRes, progressionRes, leaderboardRes, scheduledRes, activityRes, analyticsRes] = await Promise.all([
        API.get('/qurator/dashboard/stats'),
        API.get('/qurator/dashboard/project-progression'),
        API.get('/qurator/dashboard/copilot-leaderboard'),
        API.get('/qurator/dashboard/week-scheduled'), // New endpoint for 7-day calendar
        API.get('/qurator/dashboard/recent-activity'),
        API.get('/qurator/dashboard/analytics')
      ]);

      if (statsRes.data.success) setStats(statsRes.data.stats);
      if (progressionRes.data.success) setProjectProgression(progressionRes.data.processed);
      if (leaderboardRes.data.success) setCopilotLeaderboard(leaderboardRes.data.copilots);
      // if (teamsRes.data.success) setRecentTeams(teamsRes.data.teams); // Reserved for future use
      if (scheduledRes.data.success) setRecentScheduled(scheduledRes.data.scheduled);
      if (activityRes.data.success) setRecentActivity(activityRes.data.activity);
      if (analyticsRes.data.success) setAnalyticsData(analyticsRes.data.analytics);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set default values on error to prevent blank screen
      setStats({
        total_experiments: 0,
        total_projects: 0,
        total_teams: 0,
        total_qure_users: 0,
        scheduled_experiments: 0,
        completed_experiments: 0,
        published_percentage: 0,
        engagement_percentage: 0,
        reproducibility_score: 0
      });
      setProjectProgression([]);
      setCopilotLeaderboard([]);
      setRecentScheduled([]);
      setRecentActivity([]);
      setAnalyticsData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 3600) {
      return Math.floor(diffInSeconds / 60) + ' minutes ago';
    } else if (diffInSeconds < 86400) {
      return Math.floor(diffInSeconds / 3600) + ' hours ago';
    } else {
      return Math.floor(diffInSeconds / 86400) + ' days ago';
    }
  };

  const completionRate = stats.scheduled_experiments > 0 
    ? Math.round((stats.completed_experiments / stats.scheduled_experiments) * 100) 
    : 0;

  if (loading) {
    return (
      <QuratorLayout>
        <div className="dashboard-loading">
          <div className="qrt-loading"></div>
          <p>Loading dashboard...</p>
        </div>
      </QuratorLayout>
    );
  }

  return (
    <QuratorLayout>
      <div className="container-fluid px-4 py-4">
        {/* Welcome Hero Section */}
        <div className="welcome-hero animate-on-scroll">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <h1>Welcome back, {user?.username || user?.first_name || 'User'}! 👋</h1>
              <p>Manage and oversee your research projects, copilots, and team collaborations from your central dashboard.</p>
              <div className="welcome-stats d-none d-md-flex">
                <div className="welcome-stat">
                  <span className="welcome-stat-number">{stats.total_experiments}</span>
                  <span className="welcome-stat-label">Active Copilots</span>
                </div>
                <div className="welcome-stat">
                  <span className="welcome-stat-number">{stats.total_projects}</span>
                  <span className="welcome-stat-label">Projects</span>
                </div>
                <div className="welcome-stat">
                  <span className="welcome-stat-number">{stats.total_teams}</span>
                  <span className="welcome-stat-label">Teams</span>
                </div>
              </div>
            </div>
            <div className="col-lg-4 text-end d-none d-lg-block">
              <i className="bi bi-rocket-takeoff-fill" style={{ fontSize: '6rem', opacity: 0.25 }}></i>
            </div>
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="stats-grid animate-on-scroll">
          <Tooltip text="Total number of experiment copilots you have created" position="bottom">
            <div className="stat-card primary">
              <div className="stat-icon"><i className="bi bi-cpu-fill"></i></div>
              <div className="stat-number">{stats.total_experiments.toLocaleString()}</div>
              <div className="stat-label">Total Copilots</div>
            </div>
          </Tooltip>

          <Tooltip text="Number of active research projects currently running" position="bottom">
            <div className="stat-card secondary">
              <div className="stat-icon"><i className="bi bi-folder-fill"></i></div>
              <div className="stat-number">{stats.total_projects.toLocaleString()}</div>
              <div className="stat-label">Active Projects</div>
            </div>
          </Tooltip>

          <Tooltip text="Research teams you are currently managing" position="bottom">
            <div className="stat-card success">
              <div className="stat-icon"><i className="bi bi-people-fill"></i></div>
              <div className="stat-number">{stats.total_teams.toLocaleString()}</div>
              <div className="stat-label">Research Teams</div>
            </div>
          </Tooltip>

          <Tooltip text="Qure users currently active in your organisation" position="bottom">
            <div className="stat-card info">
              <div className="stat-icon"><i className="bi bi-person-check-fill"></i></div>
              <div className="stat-number">{stats.total_qure_users.toLocaleString()}</div>
              <div className="stat-label">Active Users</div>
            </div>
          </Tooltip>

          <Tooltip text="Total experiments assigned to team members" position="bottom">
            <div className="stat-card warning">
              <div className="stat-icon"><i className="bi bi-calendar-event-fill"></i></div>
              <div className="stat-number">{stats.scheduled_experiments.toLocaleString()}</div>
              <div className="stat-label">Assigned</div>
            </div>
          </Tooltip>

          <Tooltip text="Experiments fully completed by all assigned users" position="bottom">
            <div className="stat-card info">
              <div className="stat-icon"><i className="bi bi-check-circle-fill"></i></div>
              <div className="stat-number">{stats.completed_experiments.toLocaleString()}</div>
              <div className="stat-label">Assigned Completed</div>
            </div>
          </Tooltip>
        </div>

        <PendingValidationSlider
          title="Pending human validation"
          subtitle="Experiments with steps still awaiting your review — most recent first."
        />

        {/* Quick Actions */}
        <div className="content-header">
          <h2 className="content-title">
            <i className="bi bi-lightning-charge-fill" style={{ color: '#FF5722' }}></i>
            Quick Actions
          </h2>
        </div>
        <div className="quick-actions-grid animate-on-scroll">
          <Tooltip text="Create a new experiment copilot from scratch">
            <Link to="/drafts?project_id=0" className="quick-action-card">
              <div className="quick-action-icon"><i className="bi bi-plus-circle-fill"></i></div>
              <div className="quick-action-label">New Copilot</div>
            </Link>
          </Tooltip>

          <Tooltip text="Start a new research project and organise your experiments">
            <Link to="/projects" className="quick-action-card">
              <div className="quick-action-icon"><i className="bi bi-folder-plus"></i></div>
              <div className="quick-action-label">New Project</div>
            </Link>
          </Tooltip>

          <Tooltip text="Create a new research team and invite members">
            <Link to="/team-management" className="quick-action-card">
              <div className="quick-action-icon"><i className="bi bi-people-fill"></i></div>
              <div className="quick-action-label">Create Team</div>
            </Link>
          </Tooltip>

          <Tooltip text="Schedule and assign copilots to team members on the calendar">
            <Link to="/calendar" className="quick-action-card">
              <div className="quick-action-icon"><i className="bi bi-calendar-plus-fill"></i></div>
              <div className="quick-action-label">Schedule</div>
            </Link>
          </Tooltip>

          <Tooltip text="View AI-powered analytics and detailed experiment results">
            <Link to="/analytics" className="quick-action-card">
              <div className="quick-action-icon"><i className="bi bi-graph-up-arrow"></i></div>
              <div className="quick-action-label">Analytics</div>
            </Link>
          </Tooltip>

          <Tooltip text="Chat with the AI assistant for research guidance and insights">
            <Link to="/ai-assistant" className="quick-action-card">
              <div className="quick-action-icon"><i className="bi bi-robot"></i></div>
              <div className="quick-action-label">AI Assistant</div>
            </Link>
          </Tooltip>
        </div>

        {/* Main Content Grid */}
        <div className="row mb-4">
          {/* Analytics Chart */}
          <div className="col-xl-8 col-lg-7 mb-4">
            <div className="chart-container animate-on-scroll">
              <div className="content-header">
                <h3 className="content-title">
                  <i className="bi bi-graph-up" style={{ color: '#FF5722' }}></i>
                  Activity Analytics
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Tooltip text="Show month-by-month activity breakdown">
                    <button
                      className={`chart-toggle-btn ${!isAggregate ? 'active' : ''}`}
                      onClick={() => setIsAggregate(false)}
                    >
                      <i className="bi bi-bar-chart-line" style={{ marginRight: '0.3rem' }}></i>Monthly
                    </button>
                  </Tooltip>
                  <Tooltip text="Show cumulative totals growing over time">
                    <button
                      className={`chart-toggle-btn ${isAggregate ? 'active' : ''}`}
                      onClick={() => setIsAggregate(true)}
                    >
                      <i className="bi bi-layers" style={{ marginRight: '0.3rem' }}></i>Aggregate
                    </button>
                  </Tooltip>
                </div>
              </div>
              <div className="chart-wrapper">
                <canvas ref={chartRef}></canvas>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="col-xl-4 col-lg-5 mb-4">
            <div className="content-card animate-on-scroll">
              <div className="content-header">
                <h3 className="content-title">
                  <i className="bi bi-speedometer2" style={{ color: '#FF5722' }}></i>
                  Performance
                </h3>
                <Tooltip text="Live metrics calculated from your experiment analytics">
                  <i className="bi bi-info-circle" style={{ color: '#a0aec0', cursor: 'default', fontSize: '1rem' }}></i>
                </Tooltip>
              </div>

              <div className="performance-metric">
                <div className="metric-header">
                  <Tooltip text="How consistently your experiments can be reproduced by other researchers">
                    <span className="metric-label">
                      <i className="bi bi-arrow-repeat" style={{ marginRight: '0.4rem', color: '#FF5722' }}></i>
                      Overall Reproducibility Score
                    </span>
                  </Tooltip>
                  <span className="metric-value">{stats.reproducibility_score}%</span>
                </div>
                <div className="progress-bar-container">
                  <div className="progress-bar" style={{ width: `${stats.reproducibility_score}%` }}></div>
                </div>
              </div>

              <div className="performance-metric">
                <div className="metric-header">
                  <Tooltip text="Percentage of team members who are actively participating in assigned experiments">
                    <span className="metric-label">
                      <i className="bi bi-people" style={{ marginRight: '0.4rem', color: '#2196F3' }}></i>
                      Team Engagement
                    </span>
                  </Tooltip>
                  <span className="metric-value">{stats.engagement_percentage}%</span>
                </div>
                <div className="progress-bar-container">
                  <div className="progress-bar" style={{ width: `${stats.engagement_percentage}%` }}></div>
                </div>
              </div>

              <div className="performance-metric">
                <div className="metric-header">
                  <Tooltip text="Ratio of fully completed experiments to the total number of assigned experiments">
                    <span className="metric-label">
                      <i className="bi bi-check2-all" style={{ marginRight: '0.4rem', color: '#4CAF50' }}></i>
                      Completion Rate
                    </span>
                  </Tooltip>
                  <span className="metric-value">{completionRate}%</span>
                </div>
                <div className="progress-bar-container">
                  <div className="progress-bar" style={{ width: `${completionRate}%` }}></div>
                </div>
              </div>

              <div className="performance-metric">
                <div className="metric-header">
                  <Tooltip text="Overall platform efficiency score based on activity, completions and engagement metrics">
                    <span className="metric-label">
                      <i className="bi bi-lightning-charge" style={{ marginRight: '0.4rem', color: '#FF9800' }}></i>
                      Overall Efficiency
                    </span>
                  </Tooltip>
                  <span className="metric-value">87%</span>
                </div>
                <div className="progress-bar-container">
                  <div className="progress-bar" style={{ width: '87%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="row">
          {/* Project Progression */}
          <div className="col-lg-4 mb-4">
            <div className="content-card animate-on-scroll">
              <div className="content-header">
                <h3 className="content-title">
                  <i className="bi bi-folder-open" style={{ color: '#FF5722' }}></i>
                  Project Progression
                </h3>
                <Tooltip text="Recent experiments ready for analysis — click Analyze to view the full AI report">
                  <i className="bi bi-info-circle" style={{ color: '#a0aec0', cursor: 'default', fontSize: '1rem' }}></i>
                </Tooltip>
              </div>
              
              {projectProgression.length > 0 ? (
                projectProgression.map((item) => (
                  <div key={item.schedule_id} className="list-item">
                    <div className="list-item-title">{item.draft_title}</div>
                    <div className="list-item-meta">
                      <span>{item.project_name}</span>
                      <span>{formatDate(item.scheduled_date)}</span>
                    </div>
                    <div className="list-item-actions" style={{ marginTop: '0.5rem' }}>
                      <Tooltip text="Open AI-powered analytics and full report for this experiment">
                        <button
                          className="btn btn-sm btn-primary analyze-btn"
                          onClick={() => navigate(`/analytics?schedule_id=${item.schedule_id}`)}
                        >
                          <i className="bi bi-graph-up-arrow"></i> Analyze
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <i className="bi bi-folder"></i>
                  </div>
                  <div className="empty-state-title">No processed experiments</div>
                  <div className="empty-state-text">Processed experiments will appear here</div>
                </div>
              )}
            </div>
          </div>

          {/* Copilot Leaderboard */}
          <div className="col-lg-4 mb-4">
            <div className="content-card animate-on-scroll">
              <div className="content-header">
                <h3 className="content-title">
                  <i className="bi bi-trophy-fill" style={{ color: '#FF9800' }}></i>
                  Copilot Leaderboard
                </h3>
                <Tooltip text="Top copilots ranked by usage and completion rate">
                  <i className="bi bi-info-circle" style={{ color: '#a0aec0', cursor: 'default', fontSize: '1rem' }}></i>
                </Tooltip>
              </div>
              
              {copilotLeaderboard.length > 0 ? (
                copilotLeaderboard.map((copilot, index) => (
                  <div key={copilot.prt_id} className="list-item">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <span style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '50%', 
                        background: index < 3 ? 'var(--primary-gradient)' : '#e2e8f0',
                        color: index < 3 ? 'white' : '#718096',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: '700'
                      }}>
                        {index + 1}
                      </span>
                      <div className="list-item-title" style={{ flex: 1 }}>{copilot.draft_title}</div>
                    </div>
                    <div className="list-item-meta">
                      <span>Scheduled: {copilot.total_scheduled || 0}</span>
                      <span>Completed: {copilot.total_completed || 0}</span>
                      <span className={`status-badge ${copilot.is_live === 1 ? 'status-published' : 'status-draft'}`}>
                        {copilot.is_live === 1 ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <i className="bi bi-trophy"></i>
                  </div>
                  <div className="empty-state-title">No copilots yet</div>
                  <div className="empty-state-text">Create your first copilot to see the leaderboard</div>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="col-lg-4 mb-4">
            <div className="content-card animate-on-scroll">
              <div className="content-header">
                <h3 className="content-title">
                  <i className="bi bi-activity" style={{ color: '#4CAF50' }}></i>
                  Recent Activity
                </h3>
                <Tooltip text="Your most recent platform activity across all modules">
                  <i className="bi bi-info-circle" style={{ color: '#a0aec0', cursor: 'default', fontSize: '1rem' }}></i>
                </Tooltip>
              </div>
              
              <div className="activity-timeline">
                {recentActivity.length > 0 ? (
                  recentActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-icon">
                        {activity.type === 'experiment' && <i className="bi bi-flask"></i>}
                        {activity.type === 'project' && <i className="bi bi-folder"></i>}
                        {activity.type === 'team' && <i className="bi bi-people"></i>}
                      </div>
                      <div className="activity-content">
                        <div className="activity-title">
                          {activity.type === 'experiment' && 'Created Experiment'}
                          {activity.type === 'project' && 'Created Project'}
                          {activity.type === 'team' && 'Created Team'}
                        </div>
                        <div className="activity-description">{activity.title}</div>
                        <div className="activity-time">{getTimeAgo(activity.created_at)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">
                      <i className="bi bi-activity"></i>
                    </div>
                    <div className="empty-state-title">No recent activity</div>
                    <div className="empty-state-text">Your activity will appear here</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 7-Day Calendar */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="content-card animate-on-scroll">
              <div className="content-header">
                <h3 className="content-title">
                  <i className="bi bi-calendar-event" style={{ color: '#2196F3' }}></i>
                  Scheduled Experiments
                </h3>
                <Tooltip text="7-day overview of all experiments scheduled for your team members">
                  <i className="bi bi-info-circle" style={{ color: '#a0aec0', cursor: 'default', fontSize: '1rem' }}></i>
                </Tooltip>
              </div>
              
              <div className="week-calendar">
                {(() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const days = [];
                  for (let i = 0; i < 7; i++) {
                    const date = new Date(today);
                    date.setDate(today.getDate() + i);
                    days.push(date);
                  }
                  
                  // Helper function to format date as YYYY-MM-DD in local timezone
                  const formatLocalDate = (date) => {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  };
                  
                  const todayStr = formatLocalDate(today);
                  
                  return days.map((day, index) => {
                    // Format day as YYYY-MM-DD in local timezone for comparison
                    const dayStr = formatLocalDate(day);
                    const dayScheduled = recentScheduled.filter(s => {
                      if (!s.scheduled_date) return false;
                      
                      // Parse the scheduled date - handle both string and Date object
                      let scheduledDateObj;
                      if (s.scheduled_date instanceof Date) {
                        scheduledDateObj = s.scheduled_date;
                      } else if (typeof s.scheduled_date === 'string') {
                        // If it's a date string, parse it and use local timezone
                        // Handle both "YYYY-MM-DD" and "YYYY-MM-DD HH:mm:ss" formats
                        const datePart = s.scheduled_date.split(' ')[0].split('T')[0];
                        const [year, month, day] = datePart.split('-').map(Number);
                        scheduledDateObj = new Date(year, month - 1, day);
                      } else {
                        return false;
                      }
                      
                      const scheduledDateStr = formatLocalDate(scheduledDateObj);
                      return scheduledDateStr === dayStr;
                    });
                    const isToday = dayStr === todayStr;
                    
                    return (
                      <div key={index} className={`week-day ${isToday ? 'today' : ''}`}>
                        <div className="week-day-header">
                          <div className="week-day-name">{day.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}</div>
                          <div className={`week-day-number ${isToday ? 'today-number' : ''}`}>
                            {day.getDate()}
                          </div>
                        </div>
                        <div className="week-day-events">
                          {dayScheduled.length > 0 ? (
                            <>
                              {dayScheduled.slice(0, 3).map((scheduled) => (
                                <div key={scheduled.schedule_id} className="week-event-item">
                                  <div className="week-event-title">{scheduled.draft_title}</div>
                                  <div className="week-event-meta">
                                    <span>{scheduled.username}</span>
                                    {scheduled.scheduled_time && (
                                      <span>{new Date(`2000-01-01T${scheduled.scheduled_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {dayScheduled.length > 3 && (
                                <div className="week-day-more">+{dayScheduled.length - 3} more</div>
                              )}
                            </>
                          ) : (
                            <div className="week-day-empty">No events</div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </QuratorLayout>
  );
};

export default QuratorDashboard;
